import React, { useEffect, useState } from "react";
import { SkeletonGrid } from "@/components/Skeleton";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtEstDateTime } from "@/lib/time";
import { MapPin, Flame, Clock, Crown } from "lucide-react";
import ExclusiveHuntPills from "@/components/ExclusiveHuntPills";
import NextClueChip from "@/components/NextClueChip";

const FILTERS = [
  { k: "all", label: "All" },
  { k: "live", label: "Live now" },
  { k: "scheduled", label: "Upcoming" },
  { k: "found", label: "Found" },
  { k: "closed", label: "Closed" },
];

export default function DropsListPage() {
  const [drops, setDrops] = useState([]);
  const [filter, setFilter] = useState("scheduled");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    (async () => {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get("/drops", { params });
      setDrops(data.drops || []);
      setLoading(false);
    })();
  }, [filter]);

  return (
    <div className="py-8 space-y-8">
      <div>
        <div className="overline">Every hunt, past + present</div>
        <h1 className="font-display uppercase text-5xl md:text-6xl tracking-tight mt-2">
          Drop <span style={{ color: "#76B82A" }}>board</span>
        </h1>
        <p className="text-[#F5F2E8]/70 mt-3 max-w-xl">
          Scheduled, live, already claimed — every Local Loot hunt in one place.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-[#F5F2E8]/10 pb-3">
        {FILTERS.map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`drops-filter-${f.k}`}
            className={`px-3 py-1.5 font-display uppercase tracking-wide text-sm transition ${
              filter === f.k
                ? "text-[#76B82A] border-b-2 border-[#76B82A]"
                : "text-[#F5F2E8]/60 hover:text-[#F5F2E8]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <ExclusiveHuntPills />

      {loading ? (
        <SkeletonGrid count={6} />
      ) : drops.length === 0 ? (
        <div className="loot-card-flat p-12 text-center text-[#F5F2E8]/60">
          No drops match this filter yet.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {drops.map((d) => {
            const statusColor =
              d.is_golden ? "#D7A62A" :
              d.status === "live" ? "#E53935" :
              d.status === "found" ? "#D7A62A" : "#76B82A";
            return (
              <Link
                to={`/drops/${d.id}`}
                key={d.id}
                data-testid={`drops-list-card-${d.id}`}
                className="loot-card p-5 block relative"
                style={d.is_golden ? {
                  borderColor: "#D7A62A",
                  background: "linear-gradient(135deg, rgba(215,166,42,0.08), rgba(15,15,15,1) 60%)",
                } : undefined}
              >
                {d.is_golden && (
                  <Crown
                    size={18}
                    aria-hidden="true"
                    className="absolute top-3 right-3"
                    style={{ color: "#D7A62A" }}
                  />
                )}
                <div className="flex items-center gap-2">
                  <span
                    className="tag-pill"
                    style={{ color: statusColor, borderColor: statusColor }}
                  >
                    {d.is_golden && <Crown size={10} className="inline mr-1" />}
                    {d.status}
                  </span>
                  <span className="font-mono text-xs text-[#F5F2E8]/50">
                    {d.mystery_amount ? "MYSTERY LOOT" : `$${d.amount}`}
                  </span>
                </div>
                <h3 className="font-display text-2xl uppercase mt-3 tracking-tight">{d.title}</h3>
                <div className="mt-2 flex items-center gap-2 text-sm text-[#F5F2E8]/70">
                  <MapPin size={14} /> {d.city}
                </div>
                {d.status === "scheduled" && d.launch_time && (
                  <div className="mt-2 flex items-center gap-1 font-mono text-xs text-[#D7A62A]">
                    <Clock size={12} /> Live {fmtEstDateTime(d.launch_time)}
                  </div>
                )}
                {d.status === "live" && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap font-mono text-xs">
                    <span className="inline-flex items-center gap-1 text-[#E53935]">
                      <Flame size={12} /> Live right now
                    </span>
                    <NextClueChip drop={d} />
                  </div>
                )}
                <div className="mt-3 font-mono text-xs text-[#F5F2E8]/50">
                  {d.sponsor ? `Sponsored by ${d.sponsor.business_name}` : "Funded by The Local Loot"}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
